import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar";
import {
  getMyProAccess,
  formatDate,
  formatRemaining,
  formatMoney,
} from "../../services/proSystemApi";
import "./Pro.css";

// =====================================================
// MY PRO ACCESS  (Step 10, Step 11)
//
// Every creator this reader has paid, with the six columns the specification
// lists: Creator, Payment Date, Start, Expiry, Remaining Time, Status.
//
// NOTHING ON THIS PAGE IS CALCULATED HERE. The remaining time, the status and
// the expiry all arrive from the server already worked out — the same values
// the creator sees on their own "My Pro Users" screen, because both come out
// of the same function. Two screens showing the same fact from opposite sides
// cannot disagree if neither of them does the arithmetic.
//
// Tapping a card opens that creator's Pro page (Step 10's flow).
// =====================================================

const STATUS_LABEL = {
  active: "🟢 Active",
  expired: "⚪ Expired",
  refunded: "↩ Refunded",
};

function ProAccessPage() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setData(await getMyProAccess());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load your Pro access");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="pro-page-wrap">
      <Navbar />

      <div className="pro-container">
        <header className="pro-hero pro-hero-compact">
          <div className="pro-hero-badge">🔓 MY PRO ACCESS</div>
          <h1 className="pro-hero-title">Creators you have unlocked</h1>
          {data && (
            <p className="pro-hero-sub">
              {data.activeCount} active of {data.items.length} total
            </p>
          )}
        </header>

        {loading && (
          <div className="pro-loading-screen">
            <div className="pro-spinner" />
            <p>Loading…</p>
          </div>
        )}

        {!loading && error && (
          <div className="pro-card pro-empty">
            <p>{error}</p>
            <button type="button" className="pro-btn pro-btn-primary" onClick={load}>
              Try again
            </button>
          </div>
        )}

        {!loading && !error && data.items.length === 0 && (
          <div className="pro-card pro-empty">
            <p>
              You have not unlocked any creators yet. Open a creator&rsquo;s Pro page and
              pay once to read all of their Pro blogs for 30 days.
            </p>
          </div>
        )}

        {!loading && !error && data.items.length > 0 && (
          <div className="pro-access-grid">
            {data.items.map((item) => (
              <button
                key={item._id}
                type="button"
                className={`pro-card pro-access-card pro-access-${item.status}`}
                onClick={() =>
                  item.creator && navigate(`/pro-creator/${item.creator._id}`)
                }
              >
                {/* CREATOR */}
                <div className="pro-access-creator">
                  {item.creator?.profilePic ? (
                    <img src={item.creator.profilePic} alt={item.creator.name} />
                  ) : (
                    <span className="pro-blog-avatar-fallback">
                      {(item.creator?.name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div>
                    <strong>{item.creator?.name || "Unknown creator"}</strong>
                    <small>{formatMoney(item.amountRupees)} paid</small>
                  </div>
                </div>

                <span className={`pro-status-pill pro-status-${item.status}`}>
                  {STATUS_LABEL[item.status] || item.status}
                </span>

                {/* THE SIX COLUMNS */}
                <dl className="pro-kv">
                  <div>
                    <dt>Payment Date</dt>
                    <dd>{formatDate(item.paymentDate)}</dd>
                  </div>
                  <div>
                    <dt>Start</dt>
                    <dd>{formatDate(item.startDate)}</dd>
                  </div>
                  <div>
                    <dt>Expiry</dt>
                    <dd>{formatDate(item.expiryDate)}</dd>
                  </div>
                  <div>
                    <dt>Remaining</dt>
                    <dd>
                      {item.status === "active"
                        ? formatRemaining(item.remainingMs)
                        : "—"}
                    </dd>
                  </div>
                </dl>
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          className="pro-btn pro-btn-ghost pro-back"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>
    </div>
  );
}

export default ProAccessPage;
